/**
 * Pure surface-state reduction as reusable functions. The SERVER uses these
 * to keep a per-session view of current surfaces (the source of the
 * reconnect resync: connect = snapshots, then live events); the client store
 * implements the same semantics. Upsert profile: createSurface on an
 * existing id resets it, which is exactly what makes re-emission on resync
 * idempotent.
 */

import { A2UI_EVENT_NAMES } from './event-names.ts';
import type { A2uiComponentNode, DeleteSurfacePayload } from './types.ts';
import { isRecord } from './type-guards.ts';

export interface ReducedSurface {
  surfaceId: string;
  catalogId: string;
  theme?: Record<string, unknown>;
  fallbackMarkdown?: string;
  components: Map<string, A2uiComponentNode>;
  /** Run id of the turn that last touched this surface — carried through
   *  resync so a reconnecting client can re-associate surfaces with turns. */
  responseId?: string;
  previewToolCallId?: string;
  toolCallId?: string;
  /** Last accepted composition, retained only while a replacement is provisional. */
  previousSurface?: ReducedSurface;
}

/** Reconstruct one accepted composition without modifying the live surface map. */
export function replaySurface(surfaceId: string, events: unknown): ReducedSurface | null {
  if (!Array.isArray(events)) {
    return null;
  }
  const surfaces = new Map<string, ReducedSurface>();
  for (const event of events) {
    if (
      !isRecord(event) ||
      typeof event.name !== 'string' ||
      !isRecord(event.value) ||
      event.value.surfaceId !== surfaceId ||
      event.value.previewToolCallId !== undefined
    ) {
      return null;
    }
    reduceSurfaceEvent(surfaces, event.name, event.value);
  }
  const surface = surfaces.get(surfaceId);
  return surface?.components.has('root') ? surface : null;
}

/** Apply one a2ui CUSTOM event to a mutable surface map. Malformed payloads
 *  and unknown-surface updates are dropped. Returns the touched surfaceId,
 *  or null when the event was a no-op. */
export function reduceSurfaceEvent(
  surfaces: Map<string, ReducedSurface>,
  name: string,
  value: unknown,
  responseId?: string,
): string | null {
  if (name === A2UI_EVENT_NAMES.createSurface) {
    if (!isRecord(value) || typeof value.surfaceId !== 'string') {
      return null;
    }
    if (typeof value.catalogId !== 'string') {
      return null;
    }
    const existing = surfaces.get(value.surfaceId);
    const previewToolCallId = value.previewToolCallId;
    if (previewToolCallId !== undefined && typeof previewToolCallId !== 'string') {
      return null;
    }
    const previousSurface =
      existing?.previousSurface ?? (existing?.previewToolCallId ? undefined : existing);
    if (
      previewToolCallId &&
      ((previousSurface && !previousSurface.toolCallId) ||
        value.replacesToolCallId !== previousSurface?.toolCallId)
    ) {
      return null;
    }
    surfaces.set(value.surfaceId, {
      surfaceId: value.surfaceId,
      catalogId: value.catalogId,
      theme: isRecord(value.theme) ? value.theme : undefined,
      fallbackMarkdown:
        typeof value.fallbackMarkdown === 'string' ? value.fallbackMarkdown : undefined,
      components: new Map(),
      responseId: responseId || undefined,
      ...(typeof previewToolCallId === 'string' && previewToolCallId ? { previewToolCallId } : {}),
      ...(previewToolCallId && previousSurface ? { previousSurface } : {}),
    });
    return value.surfaceId;
  }

  if (name === A2UI_EVENT_NAMES.updateComponents) {
    if (!isRecord(value) || typeof value.surfaceId !== 'string') {
      return null;
    }
    const surface = surfaces.get(value.surfaceId);
    if (!surface || !Array.isArray(value.components)) {
      return null;
    }
    if (value.previewToolCallId !== surface.previewToolCallId) {
      return null;
    }
    for (const node of value.components) {
      if (!isRecord(node) || typeof node.id !== 'string' || typeof node.component !== 'string') {
        continue;
      }
      surface.components.set(node.id, node as A2uiComponentNode);
    }
    if (responseId) {
      surface.responseId = responseId;
    }
    if (!surface.previewToolCallId && typeof value.toolCallId === 'string') {
      surface.toolCallId = value.toolCallId;
    }
    return value.surfaceId;
  }

  if (name === A2UI_EVENT_NAMES.deleteSurface) {
    if (!isRecord(value) || typeof value.surfaceId !== 'string') {
      return null;
    }
    if (
      value.previewToolCallId !== undefined &&
      surfaces.get(value.surfaceId)?.previewToolCallId !== value.previewToolCallId
    ) {
      return null;
    }
    const previousSurface = surfaces.get(value.surfaceId)?.previousSurface;
    if (value.previewToolCallId && previousSurface) {
      surfaces.set(value.surfaceId, previousSurface);
      return value.surfaceId;
    }
    return surfaces.delete(value.surfaceId) ? value.surfaceId : null;
  }

  return null;
}

/** Re-express a reduced surface as the A2UI messages that rebuild it — the
 *  resync payload for a (re)connecting client. Idempotent under the upsert
 *  profile. */
export function surfaceToResyncEvents(
  surface: ReducedSurface,
): Array<{ name: string; value: unknown; responseId?: string }> {
  const previous = surface.previousSurface;
  const events = previous
    ? surfaceToResyncEvents(previous).map((event) => ({
        ...event,
        responseId: previous.responseId ?? '',
      }))
    : [];
  return [
    ...events,
    {
      name: A2UI_EVENT_NAMES.createSurface,
      value: {
        surfaceId: surface.surfaceId,
        catalogId: surface.catalogId,
        ...(surface.previewToolCallId ? { previewToolCallId: surface.previewToolCallId } : {}),
        ...(previous?.toolCallId ? { replacesToolCallId: previous.toolCallId } : {}),
        ...(surface.theme ? { theme: surface.theme } : {}),
        ...(surface.fallbackMarkdown ? { fallbackMarkdown: surface.fallbackMarkdown } : {}),
      },
    },
    {
      name: A2UI_EVENT_NAMES.updateComponents,
      value: {
        surfaceId: surface.surfaceId,
        components: [...surface.components.values()],
        ...(surface.previewToolCallId ? { previewToolCallId: surface.previewToolCallId } : {}),
        ...(surface.toolCallId ? { toolCallId: surface.toolCallId } : {}),
      },
    },
  ];
}

/** Discard only unfinished previews of the terminating run; accepted replacements are untouched. */
export function surfacePreviewCleanupEvents(
  surfaces: Iterable<{
    surfaceId: string;
    responseId?: string;
    lastTouchedResponseId?: string;
    previewToolCallId?: string;
  }>,
  responseId: string,
): Array<{ name: string; value: DeleteSurfacePayload }> {
  const events: Array<{ name: string; value: DeleteSurfacePayload }> = [];
  for (const surface of surfaces) {
    if (
      (surface.lastTouchedResponseId ?? surface.responseId) === responseId &&
      surface.previewToolCallId
    ) {
      events.push({
        name: A2UI_EVENT_NAMES.deleteSurface,
        value: { surfaceId: surface.surfaceId, previewToolCallId: surface.previewToolCallId },
      });
    }
  }
  return events;
}
